import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';

export enum PrivacyLevel {
  ANONYMOUS = 'ANONYMOUS',     // Default - no data collection
  OPTED_IN = 'OPTED_IN',       // User consented to anonymous telemetry
  FULL_ANALYTICS = 'FULL_ANALYTICS' // Not used - for future if needed
}

export interface PrivacyConsent {
  telemetry: boolean;
  analytics: boolean;
  cookies: 'necessary' | 'functional' | 'all';
  timestamp?: string;
  version?: string;
}

export interface PrivacyState {
  level: PrivacyLevel;
  consent: PrivacyConsent;
  transparencyViewed: boolean;
  privacyScore: number; // 0-100 score for privacy protection
  dataCollectionTypes: string[];
  lastUpdated?: string;
}

const initialState: PrivacyState = {
  level: PrivacyLevel.ANONYMOUS, // Privacy-first default
  consent: {
    telemetry: false,
    analytics: false,
    cookies: 'necessary',
  },
  transparencyViewed: false,
  privacyScore: 100, // Maximum privacy by default
  dataCollectionTypes: [],
  lastUpdated: new Date().toISOString(),
};

const privacySlice = createSlice({
  name: 'privacy',
  initialState,
  reducers: {
    setPrivacyLevel: (state, action: PayloadAction<PrivacyLevel>) => {
      state.level = action.payload;
      state.lastUpdated = new Date().toISOString();
      
      // Update privacy score based on level
      switch (action.payload) {
        case PrivacyLevel.ANONYMOUS:
          state.privacyScore = 100;
          state.dataCollectionTypes = [];
          break;
        case PrivacyLevel.OPTED_IN:
          state.privacyScore = 85;
          state.dataCollectionTypes = ['anonymous-telemetry'];
          break;
        case PrivacyLevel.FULL_ANALYTICS:
          state.privacyScore = 60;
          state.dataCollectionTypes = ['anonymous-telemetry', 'usage-analytics'];
          break;
      }
    },
    
    updateConsent: (state, action: PayloadAction<Partial<PrivacyConsent>>) => {
      state.consent = {
        ...state.consent,
        ...action.payload,
        timestamp: new Date().toISOString(),
      };
      state.lastUpdated = new Date().toISOString();
      
      // Update privacy level based on consent
      if (state.consent.telemetry || state.consent.analytics) {
        state.level = PrivacyLevel.OPTED_IN;
      } else {
        state.level = PrivacyLevel.ANONYMOUS;
      }
    },
    
    setTransparencyViewed: (state) => {
      state.transparencyViewed = true;
    },
    
    revokeAllConsent: (state) => {
      state.level = PrivacyLevel.ANONYMOUS;
      state.consent = {
        telemetry: false,
        analytics: false,
        cookies: 'necessary',
        timestamp: new Date().toISOString(),
      };
      state.privacyScore = 100;
      state.dataCollectionTypes = [];
      state.lastUpdated = new Date().toISOString();
    },
    
    acceptPrivacyTerms: (state, action: PayloadAction<{ version: string }>) => {
      state.consent.version = action.payload.version;
      state.consent.timestamp = new Date().toISOString();
      state.lastUpdated = new Date().toISOString();
    },
  },
});

export const {
  setPrivacyLevel,
  updateConsent,
  setTransparencyViewed,
  revokeAllConsent,
  acceptPrivacyTerms,
} = privacySlice.actions;

// Additional exports for ConsentManager
export const updatePrivacyLevel = setPrivacyLevel;
export const acceptPrivacyGuarantee = acceptPrivacyTerms;
export const setShowConsentBanner = (show: boolean) => ({
  type: 'privacy/setShowConsentBanner',
  payload: show,
});
export const setShowPrivacyDashboard = (show: boolean) => ({
  type: 'privacy/setShowPrivacyDashboard',
  payload: show,
});

// Selectors
export const selectPrivacyLevel = (state: RootState) => state.privacy.level;
export const selectPrivacyConsent = (state: RootState) => state.privacy.consent;
export const selectPrivacyScore = (state: RootState) => state.privacy.privacyScore;
export const selectDataCollectionTypes = (state: RootState) => state.privacy.dataCollectionTypes;
export const selectIsAnonymous = (state: RootState) => state.privacy.level === PrivacyLevel.ANONYMOUS;
export const selectHasTelemetryConsent = (state: RootState) => state.privacy.consent.telemetry;
export const selectTransparencyViewed = (state: RootState) => state.privacy.transparencyViewed;

export default privacySlice.reducer;